<template>
  <div class="flex flex-col h-full overflow-hidden">
    <report-progress :showProgress="isLoading" />

    <!-- Stats summary cards -->
    <div v-if="stats" class="flex flex-wrap gap-3 px-1 pb-3 flex-shrink-0">
      <div class="bg-white rounded-xl border border-gray-200 px-4 py-3 min-w-[150px]">
        <div class="text-xs font-medium text-gray-500 uppercase tracking-wide">
          {{ $t("reports.voice_agent_cdr.stat_total_calls") }}
        </div>
        <div class="text-2xl font-bold text-primary-900 mt-0.5">
          {{ stats.total_calls ?? "-" }}
        </div>
      </div>
      <div class="bg-white rounded-xl border border-gray-200 px-4 py-3 min-w-[150px]">
        <div class="text-xs font-medium text-gray-500 uppercase tracking-wide">
          {{ $t("reports.voice_agent_cdr.stat_avg_duration") }}
        </div>
        <div class="text-2xl font-bold text-primary-900 mt-0.5">
          {{ formatDuration(stats.avg_duration) }}
        </div>
      </div>
    </div>

    <div class="flex-1 overflow-hidden">
      <div class="h-full flex flex-col">
        <div class="flex-1 overflow-auto">
          <table class="w-full table-auto text-sm">
            <thead class="sticky top-0 z-10">
              <tr>
                <th class="!bg-primary-900 text-white set-border text-center font-weight-medium rounded-s-lg mx-1 px-2 py-2">
                  {{ $t("reports.voice_agent_cdr.head_call_id") }}
                </th>
                <th class="!bg-primary-900 text-white set-border text-center font-weight-medium px-2 py-2">
                  {{ $t("reports.voice_agent_cdr.head_caller") }}
                </th>
                <th class="!bg-primary-900 text-white set-border text-center font-weight-medium px-2 py-2">
                  {{ $t("reports.voice_agent_cdr.head_dialed") }}
                </th>
                <th class="!bg-primary-900 text-white set-border text-center font-weight-medium px-2 py-2">
                  {{ $t("reports.voice_agent_cdr.head_duration") }}
                </th>
                <th class="!bg-primary-900 text-white set-border text-center font-weight-medium px-2 py-2">
                  {{ $t("reports.voice_agent_cdr.head_started") }}
                </th>
                <th class="!bg-primary-900 text-white set-border text-center font-weight-medium px-2 py-2">
                  {{ $t("reports.voice_agent_cdr.head_ended") }}
                </th>
                <th class="!bg-primary-900 text-white set-border text-center font-weight-medium px-2 py-2">
                  {{ $t("reports.voice_agent_cdr.head_tags") }}
                </th>
                <th class="!bg-primary-900 text-white set-border text-center font-weight-medium rounded-e-lg px-2 py-2"></th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(call, index) in reportData"
                :key="call.call_id || index"
                :class="index % 2 === 0 ? 'bg-white' : 'bg-gray-50'"
              >
                <td class="text-center !py-2 px-2 border-b border-gray-200 break-all font-mono text-xs">
                  {{ call.call_id }}
                </td>
                <td class="text-center text-indigo px-2 border-b border-gray-200" dir="ltr">
                  {{ call.caller_number || "-" }}
                </td>
                <td class="text-center text-indigo px-2 border-b border-gray-200" dir="ltr">
                  {{ call.dialed_number || "-" }}
                </td>
                <td class="text-center text-primary-900 px-2 border-b border-gray-200">
                  {{ formatDuration(call.duration) }}
                </td>
                <td class="text-center px-2 border-b border-gray-200">
                  {{ call.started_at || "-" }}
                </td>
                <td class="text-center px-2 border-b border-gray-200">
                  {{ call.ended_at || "-" }}
                </td>
                <td class="px-2 border-b border-gray-200">
                  <div
                    v-if="callTags(call).length"
                    class="flex flex-wrap justify-center items-center gap-1"
                  >
                    <span
                      v-for="(tag, tIdx) in callTags(call)"
                      :key="tIdx"
                      class="inline-block px-2 py-0.5 rounded-full text-xs bg-primary-50 text-primary-900 border border-primary-100 whitespace-nowrap"
                    >
                      {{ tag }}
                    </span>
                  </div>
                </td>
                <td class="px-2 border-b border-gray-200">
                  <div class="flex justify-center items-center gap-1">
                    <div
                      class="bg-primary-900 rounded hover:bg-primary-500 p-1 text-white cursor-pointer"
                      :title="$t('reports.voice_agent_cdr.view_details')"
                      @click="openDetails(call)"
                    >
                      <v-icon size="small">mdi-text-box-search-outline</v-icon>
                    </div>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import ReportProgress from "../../components/ReportProgress.vue";

export default {
  name: "voice-agent-cdr-report",
  components: { ReportProgress },
  props: {
    selectedFilters: { type: Object, default: () => ({}) },
    reportData: { type: Array, default: () => [] },
    totalCount: { type: Number, default: 0 },
    showCount: { type: Boolean, default: false },
    isLoading: { type: Boolean, default: false },
  },
  data() {
    return {
      stats: null,
    };
  },
  methods: {
    ...mapActions(["GetVoiceCDRStats"]),

    formatDuration(seconds) {
      const total = Math.floor(Number(seconds));
      if (!Number.isFinite(total) || total < 0) return "-";
      const h = Math.floor(total / 3600);
      const m = Math.floor((total % 3600) / 60);
      const s = total % 60;
      const pad = (n) => String(n).padStart(2, "0");
      return h > 0 ? `${pad(h)}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
    },

    callTags(call) {
      const t = call?.tags;
      return Array.isArray(t) ? t.filter(Boolean) : [];
    },

    openDetails(call) {
      this.$router.push({
        name: "voice-cdr-details",
        query: { call_id: call.call_id },
      });
    },

    async fetchStats() {
      if (!this.selectedFilters?.from || !this.selectedFilters?.to) return;
      try {
        const resp = await this.GetVoiceCDRStats({
          from: this.selectedFilters.from,
          to: this.selectedFilters.to,
        });
        this.stats = resp && resp.success ? resp.stats : null;
      } catch (e) {
        this.stats = null;
      }
    },
  },
  mounted() {
    this.fetchStats();
  },
};
</script>

<style scoped></style>
